// ═══════════════════════════════════════════════════════════
// Workflow Store — V2 API backed
// Templates, Instances, Steps with full CRUD
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';

const FWT = FIELDS.workflowTemplates;
const FWI = FIELDS.workflowInstances;
const FWS = FIELDS.workflowSteps;

// ── Types ──────────────────────────────────────────────

export interface WFTemplate {
  id: number;
  Rid: number;          // alias for id
  name: string;
  description: string;
  triggerType: string;
  triggerTable: string;
  triggerField: string;
  triggerValue: string;
  stepsJson: string;
  isActive: boolean;
  active: string;       // 'yes' | 'no' — WorkflowTemplate compat
  createdBy: string;
  creatorId: string;    // alias for createdBy
  module: string;       // derived from triggerTable context
  category: string;     // grouping field
  priority: string;     // priority field
  notes: string;        // notes field
}

export interface WFInstance {
  id: number;
  Rid?: number;              // alias
  templateId: number;
  templateName: string;
  status: string;
  currentStep: string;
  currentStepIndex?: number; // alias/computed
  contextJson: string;
  startedBy: string;
  startedAt: string;
  completedAt: string;
  sourceModule: string;
  sourceRecordId: string;
  sourceTable?: string;      // compat field
  notes?: string;            // compat field
  // Derived fields
  sourceRecordLabel: string;
  totalSteps: number;
  priority: string;
}

export interface WFStep {
  id: number;
  Rid: number;           // alias for id — required by WorkflowStep compat
  instanceId: string;    // stored as string for WorkflowStep compat
  stepIndex: string;     // stored as string for WorkflowStep compat
  stepName: string;
  stepType: string;
  assignedRole: string;
  assignedUser: string;
  assignee: string;      // alias for assignedUser
  assigneeName: string;  // resolved display name
  assigneeType: string;  // 'role' | 'user'
  status: string;
  completedBy: string;
  completedAt: string;
  notes: string;
  comments: string;      // alias for notes
  // Derived
  outcome: string;
  dueDate: string;
  instructions?: string;
  config?: Record<string, unknown>;
  // WorkflowStep compat fields
  notifyChannels: string;
  escalationHours: string;
  escalationAction: string;
  escalationTarget: string;
  configJson: string;
  signatureData: string;
}

export interface StepDef {
  name: string;
  type: string;
  assignee: string;
  assigneeType: string;
  instructions?: string;
  config?: Record<string, unknown>;
  documentChecklist?: Array<{ key: string; label: string; required: boolean; description?: string }>;
}

// ── Step type constants ────────────────────────────────

export const STEP_TYPE_LABELS: Record<string, string> = {
  approval: 'Approval',
  review: 'Review',
  task: 'Task',
  signature: 'Signature',
  notification: 'Notification',
  conditional: 'Conditional',
};

export const STEP_TYPE_COLORS: Record<string, string> = {
  approval: '#059669',
  review: '#0284C7',
  task: '#D97706',
  signature: '#7C3AED',
  notification: '#6366F1',
  conditional: '#64748B',
};

export const PRIORITY_COLORS: Record<string, string> = {
  critical: '#DC2626',
  high: '#D97706',
  normal: '#0284C7',
  low: '#64748B',
};

export const INSTANCE_STATUS_COLORS: Record<string, string> = {
  active: '#0284C7',
  completed: '#059669',
  cancelled: '#64748B',
  escalated: '#DC2626',
  paused: '#D97706',
};

// ── Field Mappers ──────────────────────────────────────

function mapTemplate(r: Record<string, unknown>): WFTemplate {
  const isActive = Boolean(r[FWT.isActive]);
  const rawStepsJson = (r[FWT.stepsJson] as string) || '[]';

  // Extract _meta (module/category/priority) embedded alongside steps.
  // Legacy templates store a plain array; new ones store { _meta, steps }.
  let module = '', category = '', priority = 'normal', stepsJson = rawStepsJson;
  try {
    const parsed = JSON.parse(rawStepsJson);
    if (parsed && !Array.isArray(parsed) && parsed._meta) {
      module   = String(parsed._meta.module   ?? '');
      category = String(parsed._meta.category ?? '');
      priority = String(parsed._meta.priority ?? 'normal');
      stepsJson = JSON.stringify(parsed.steps ?? []);
    }
  } catch { /* keep defaults */ }

  return {
    id: r.record_id as number,
    Rid: r.record_id as number,
    name: (r[FWT.name] as string) || '',
    description: (r[FWT.description] as string) || '',
    triggerType: (r[FWT.triggerType] as string) || '',
    triggerTable: (r[FWT.triggerTable] as string) || '',
    triggerField: (r[FWT.triggerField] as string) || '',
    triggerValue: (r[FWT.triggerValue] as string) || '',
    stepsJson,
    isActive,
    active: isActive ? 'yes' : 'no',
    createdBy: (r[FWT.createdBy] as string) || '',
    creatorId: (r[FWT.createdBy] as string) || '',
    module,
    category,
    priority,
    notes: '',
  };
}

function mapInstance(r: Record<string, unknown>): WFInstance {
  const contextJson = (r[FWI.contextJson] as string) || '';
  let sourceRecordLabel = '';
  let priority = 'normal';
  try {
    const ctx = JSON.parse(contextJson);
    sourceRecordLabel = ctx?.sourceRecordLabel || ctx?.recordLabel || '';
    priority = ctx?.priority || 'normal';
  } catch { /* */ }

  return {
    id: r.record_id as number,
    Rid: r.record_id as number,
    templateId: Number(r[FWI.templateId]) || 0,
    templateName: (r[FWI.templateName] as string) || '',
    status: (r[FWI.status] as string) || 'active',
    currentStep: String(r[FWI.currentStep] ?? '0'),
    contextJson,
    startedBy: (r[FWI.startedBy] as string) || '',
    startedAt: (r[FWI.startedAt] as string) || '',
    completedAt: (r[FWI.completedAt] as string) || '',
    sourceModule: (r[FWI.sourceModule] as string) || '',
    sourceRecordId: (r[FWI.sourceRecordId] as string) || '',
    sourceTable: '',
    notes: '',
    sourceRecordLabel,
    totalSteps: 0, // filled after templates load
    priority,
  };
}

function mapStep(r: Record<string, unknown>): WFStep {
  const notes = (r[FWS.notes] as string) || '';
  // Parse outcome from notes prefix "[outcome:xxx]"
  let outcome = '';
  const outcomeMatch = notes.match(/^\[outcome:(\w+)\]/);
  if (outcomeMatch) outcome = outcomeMatch[1];

  return {
    id: r.record_id as number,
    instanceId: String(r[FWS.instanceId] || ''),
    stepIndex: String(r[FWS.stepIndex] || '0'),
    stepName: (r[FWS.stepName] as string) || '',
    stepType: (r[FWS.stepType] as string) || '',
    assignedRole: (r[FWS.assignedRole] as string) || '',
    assignedUser: (r[FWS.assignedUser] as string) || '',
    assignee: (r[FWS.assignedRole] as string) || (r[FWS.assignedUser] as string) || '',
    assigneeName: '',
    assigneeType: (r[FWS.assignedRole] as string) ? 'role' : ((r[FWS.assignedUser] as string) ? 'user' : ''),
    status: (r[FWS.status] as string) || 'pending',
    completedBy: (r[FWS.completedBy] as string) || '',
    completedAt: (r[FWS.completedAt] as string) || '',
    notes: outcomeMatch ? notes.slice(outcomeMatch[0].length).trim() : notes,
    comments: outcomeMatch ? notes.slice(outcomeMatch[0].length).trim() : notes,
    outcome,
    dueDate: '', // V2 schema has no due date column; could be in context
    Rid: r.record_id as number,
    notifyChannels: '', escalationHours: '', escalationAction: '', escalationTarget: '',
    configJson: '', signatureData: '',
  };
}

// ── Store Interface ────────────────────────────────────

interface WorkflowState {
  templates: WFTemplate[];
  instances: WFInstance[];
  steps: WFStep[];
  loading: boolean;
  instancesTotal: number;
  instancesPage: number;

  fetchTemplates: () => Promise<void>;
  fetchInstances: (page?: number) => Promise<void>;
  fetchSteps: (instanceId?: number) => Promise<void>;
  setInstancesPage: (page: number) => void;
  loadAll: () => Promise<void>;

  createTemplate: (data: Record<string, unknown>) => Promise<number | null>;
  updateTemplate: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteTemplate: (id: number) => Promise<void>;

  startWorkflow: (templateId: number, sourceModule: string, sourceRecordId: string, sourceRecordLabel: string, startedBy: string, contextData?: Record<string, unknown>) => Promise<number | null>;
  createAdHocTask: (title: string, description: string, assigneeEmail: string, priority: string, startedBy: string) => Promise<number | null>;
  updateInstance: (id: number, data: Partial<Pick<WFInstance, 'status' | 'currentStep' | 'completedAt'>>) => Promise<void>;

  completeStep: (stepId: number, outcome: string, completedBy: string, comments?: string) => Promise<void>;
  rejectStep: (stepId: number, completedBy: string, comments?: string) => Promise<void>;
  updateStep: (stepId: number, data: Record<string, unknown>) => Promise<void>;

  // V1 compat aliases
  isLoading: boolean;
  loadWfTemplates: () => Promise<void>;
  loadInstances: () => Promise<void>;
  loadSteps: (instanceId?: number) => Promise<void>;
  loadTemplates: () => Promise<void>;
}

// ── Store Implementation ───────────────────────────────

export const useWorkflowStore = create<WorkflowState>((set, get) => ({
  templates: [],
  instances: [],
  steps: [],
  loading: false,
  instancesTotal: 0,
  instancesPage: 1,

  fetchTemplates: async () => {
    try {
      // Templates are global config — fetch all, unfiltered
      const r = await fetchRecords(TABLES.workflowTemplates, {
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ templates: r.records.map(mapTemplate) });
    } catch (e) {
      console.error('[WF] fetchTemplates', e);
    }
  },

  fetchInstances: async (page?: number) => {
    try {
      const p = page ?? get().instancesPage;
      const PAGE_SIZE = 100;
      const activeIncident = (await import('./authStore')).useAuthStore.getState().activeIncident;
      const incFilters = FWI.incidentId && activeIncident
        ? [{ field: FWI.incidentId, operator: 'eq' as const, value: activeIncident.id }]
        : [];
      const r = await queryRecords<Record<string, unknown>>(TABLES.workflowInstances, {
        limit: PAGE_SIZE,
        offset: (p - 1) * PAGE_SIZE,
        sort: { field: FWI.startedAt, direction: 'desc' },
        filters: incFilters,
      });
      const templates = get().templates;
      const instances = r.records.map(mapInstance).map(inst => {
        // Fill totalSteps from template stepsJson
        const tmpl = templates.find(t => t.id === inst.templateId);
        if (tmpl) {
          try {
            const defs = JSON.parse(tmpl.stepsJson);
            inst.totalSteps = Array.isArray(defs) ? defs.length : 0;
          } catch { /* */ }
        }
        return inst;
      });
      set({ instances, instancesTotal: r.total, instancesPage: p });
    } catch (e) {
      console.error('[WF] fetchInstances', e);
    }
  },

  setInstancesPage: (page: number) => {
    set({ instancesPage: page });
    get().fetchInstances(page);
  },

  fetchSteps: async (instanceId?: number) => {
    try {
      if (instanceId) {
        // Server-side filter by instanceId — no client-side filtering needed
        const r = await queryRecords<Record<string, unknown>>(TABLES.workflowSteps, {
          filters: [{ field: FWS.instanceId, operator: 'eq', value: instanceId }],
          limit: 100,
          sort: { field: FWS.stepIndex, direction: 'asc' },
        });
        const steps = r.records.map(mapStep);
        // Merge: replace steps for this instance, keep others
        const existing = get().steps.filter(s => s.instanceId !== String(instanceId));
        set({ steps: [...existing, ...steps] });
      } else {
        // Bulk fetch for inbox / overview — capped at 500 most recent
        const r = await queryRecords<Record<string, unknown>>(TABLES.workflowSteps, {
          limit: 500,
          sort: { field: 'created_at', direction: 'desc' },
        });
        set({ steps: r.records.map(mapStep) });
      }
    } catch (e) {
      console.error('[WF] fetchSteps', e);
    }
  },

  loadAll: async () => {
    set({ loading: true });
    try {
      await get().fetchTemplates();
      await get().fetchInstances();
      await get().fetchSteps();
    } finally {
      set({ loading: false });
    }
  },

  createTemplate: async (data) => {
    try {
      const r = await createRecord<{ record_id: number }>(TABLES.workflowTemplates, data);
      await get().fetchTemplates();
      return r.record_id;
    } catch (e) {
      console.error('[WF] createTemplate', e);
      return null;
    }
  },

  updateTemplate: async (id, data) => {
    try {
      await updateRecord(TABLES.workflowTemplates, id, data);
      await get().fetchTemplates();
    } catch (e) {
      console.error('[WF] updateTemplate', e);
    }
  },

  deleteTemplate: async (id) => {
    try {
      await deleteRecord(TABLES.workflowTemplates, id);
      set({ templates: get().templates.filter(t => t.id !== id) });
    } catch (e) {
      console.error('[WF] deleteTemplate', e);
    }
  },

  startWorkflow: async (templateId, sourceModule, sourceRecordId, sourceRecordLabel, startedBy, contextData) => {
    try {
      const tmpl = get().templates.find(t => t.id === templateId);
      if (!tmpl) throw new Error('Template not found');

      let defs: StepDef[] = [];
      try { defs = JSON.parse(tmpl.stepsJson); } catch { /* */ }

      // Create instance
      const ctx = {
        ...(contextData || {}),
        sourceRecordLabel,
        manual: !contextData,
      };
      const activeIncident = (await import('./authStore')).useAuthStore.getState().activeIncident;
      const instancePayload: Record<string, unknown> = {
        [FWI.templateId]: templateId,
        [FWI.templateName]: tmpl.name,
        [FWI.status]: 'active',
        [FWI.currentStep]: '0',
        [FWI.contextJson]: JSON.stringify(ctx),
        [FWI.startedBy]: startedBy,
        [FWI.startedAt]: new Date().toISOString(),
        [FWI.sourceModule]: sourceModule,
        [FWI.sourceRecordId]: sourceRecordId,
      };
      // Phase 6 — Link to active incident when field is provisioned
      if (FWI.incidentId && activeIncident?.id) instancePayload[FWI.incidentId] = String(activeIncident.id);
      const instRes = await createRecord<{ record_id: number }>(TABLES.workflowInstances, instancePayload);

      const instanceId = instRes.record_id;

      // Create step records — notification steps with targetRoles expand to one step per role
      let stepIndex = 0;
      for (let i = 0; i < defs.length; i++) {
        const def = defs[i];
        const targetRoles: string[] = def.config?.targetRoles ?? [];
        if (def.type === 'notification' && targetRoles.length > 0) {
          for (const role of targetRoles) {
            await createRecord(TABLES.workflowSteps, {
              [FWS.instanceId]: instanceId,
              [FWS.stepIndex]: stepIndex,
              [FWS.stepName]: def.name || `Step ${i + 1}`,
              [FWS.stepType]: 'notification',
              [FWS.assignedRole]: role,
              [FWS.assignedUser]: '',
              [FWS.status]: 'in_progress',
              [FWS.notes]: def.config?.message || '',
            });
            stepIndex++;
          }
        } else {
          await createRecord(TABLES.workflowSteps, {
            [FWS.instanceId]: instanceId,
            [FWS.stepIndex]: stepIndex,
            [FWS.stepName]: def.name || `Step ${i + 1}`,
            [FWS.stepType]: def.type || 'task',
            [FWS.assignedRole]: def.assigneeType === 'role' ? def.assignee : '',
            [FWS.assignedUser]: def.assigneeType !== 'role' ? def.assignee : '',
            [FWS.status]: stepIndex === 0 ? 'in_progress' : 'pending',
          });
          stepIndex++;
        }
      }

      await get().fetchInstances();
      await get().fetchSteps();
      return instanceId;
    } catch (e) {
      console.error('[WF] startWorkflow', e);
      return null;
    }
  },

  createAdHocTask: async (title, description, assigneeEmail, priority, startedBy) => {
    try {
      const activeIncident = (await import('./authStore')).useAuthStore.getState().activeIncident;
      const ctx = { sourceRecordLabel: title, description, priority, manual: true };
      const instancePayload: Record<string, unknown> = {
        [FWI.templateId]: 0,
        [FWI.templateName]: 'Ad Hoc Task',
        [FWI.status]: 'active',
        [FWI.currentStep]: '0',
        [FWI.contextJson]: JSON.stringify(ctx),
        [FWI.startedBy]: startedBy,
        [FWI.startedAt]: new Date().toISOString(),
        [FWI.sourceModule]: '*',
        [FWI.sourceRecordId]: '',
      };
      if (FWI.incidentId && activeIncident?.id) instancePayload[FWI.incidentId] = String(activeIncident.id);
      const instRes = await createRecord<{ record_id: number }>(TABLES.workflowInstances, instancePayload);
      const instanceId = instRes.record_id;
      await createRecord(TABLES.workflowSteps, {
        [FWS.instanceId]: instanceId,
        [FWS.stepIndex]: '0',
        [FWS.stepName]: title,
        [FWS.stepType]: 'task',
        [FWS.assignedUser]: assigneeEmail,
        [FWS.assignedRole]: '',
        [FWS.status]: 'in_progress',
        [FWS.notes]: description,
      });
      await get().fetchInstances();
      await get().fetchSteps();
      return instanceId;
    } catch (e) {
      console.error('[WF] createAdHocTask', e);
      return null;
    }
  },

  updateInstance: async (id, data) => {
    try {
      const payload: Record<string, unknown> = {};
      if (data.status !== undefined) payload[FWI.status] = data.status;
      if (data.currentStep !== undefined) payload[FWI.currentStep] = data.currentStep;
      if (data.completedAt !== undefined) payload[FWI.completedAt] = data.completedAt;
      await updateRecord(TABLES.workflowInstances, id, payload);
      await get().fetchInstances();
    } catch (e) {
      console.error('[WF] updateInstance', e);
    }
  },

  completeStep: async (stepId, outcome, completedBy, comments) => {
    try {
      const notesVal = `[outcome:${outcome}]${comments ? ' ' + comments : ''}`;
      await updateRecord(TABLES.workflowSteps, stepId, {
        [FWS.status]: 'completed',
        [FWS.completedBy]: completedBy,
        [FWS.completedAt]: new Date().toISOString(),
        [FWS.notes]: notesVal,
      });

      // Advance to next step
      const step = get().steps.find(s => s.id === stepId);
      if (step) {
        const instanceSteps = get().steps
          .filter(s => s.instanceId === step.instanceId)
          .sort((a, b) => Number(a.stepIndex) - Number(b.stepIndex));
        const nextStep = instanceSteps.find(s => Number(s.stepIndex) > Number(step.stepIndex) && s.status === 'pending');
        const instance = get().instances.find(i => String(i.id) === step.instanceId);

        if (nextStep) {
          // Activate next step
          await updateRecord(TABLES.workflowSteps, nextStep.id, {
            [FWS.status]: 'in_progress',
          });
          if (instance) {
            await get().updateInstance(instance.id, { currentStep: String(nextStep.stepIndex) });
          }
        } else if (instance) {
          // All steps done — complete instance
          await get().updateInstance(instance.id, {
            status: 'completed',
            completedAt: new Date().toISOString(),
          });
        }
      }

      await get().fetchSteps();
      await get().fetchInstances();
    } catch (e) {
      console.error('[WF] completeStep', e);
    }
  },

  rejectStep: async (stepId, completedBy, comments) => {
    try {
      const notesVal = `[outcome:rejected]${comments ? ' ' + comments : ''}`;
      await updateRecord(TABLES.workflowSteps, stepId, {
        [FWS.status]: 'rejected',
        [FWS.completedBy]: completedBy,
        [FWS.completedAt]: new Date().toISOString(),
        [FWS.notes]: notesVal,
      });

      // Reject the whole instance
      const step = get().steps.find(s => s.id === stepId);
      if (step) {
        const instance = get().instances.find(i => String(i.id) === step.instanceId);
        if (instance) {
          await get().updateInstance(instance.id, {
            status: 'cancelled',
            completedAt: new Date().toISOString(),
          });
        }
      }

      await get().fetchSteps();
      await get().fetchInstances();
    } catch (e) {
      console.error('[WF] rejectStep', e);
    }
  },

  updateStep: async (stepId, data) => {
    try {
      const payload: Record<string, unknown> = {};
      if (data.assignedRole !== undefined) payload[FWS.assignedRole] = data.assignedRole;
      if (data.assignedUser !== undefined) payload[FWS.assignedUser] = data.assignedUser;
      if (data.status !== undefined) payload[FWS.status] = data.status;
      await updateRecord(TABLES.workflowSteps, stepId, payload);
      await get().fetchSteps();
    } catch (e) {
      console.error('[WF] updateStep', e);
    }
  },

  // V1 compat aliases
  isLoading: false,
  loadWfTemplates: async () => { await get().fetchTemplates(); },
  loadInstances: async () => { await get().fetchInstances(); },
  loadSteps: async (instanceId?: number) => { await get().fetchSteps(instanceId); },
  loadTemplates: async () => { await get().fetchTemplates(); },
}));
